import type { FiredEffect, TraceNode } from '@cyberscore/contracts';
import { CheckCircle2, FlaskConical, MinusCircle, XCircle } from 'lucide-react';
import { useState } from 'react';
import { href } from '../../lib/router';
import { useSession } from '../../lib/session';
import { AnswerInput } from '../assessments/AnswerInput';
import { useAssessments } from '../assessments/api';
import { useDryRun, useItemBodies, useQuestionInfo, useStudioAccess, type DryRunResult } from './api';
import { ConditionEditor } from './ConditionEditor';
import type { Cond } from './conditions';
import { useItemDraft } from './draft';
import { previewQuestion, ruleIssues, showActual, type QuestionDraft, type RuleDraft } from './editors';
import { EditorHead, IssueList } from './EditorParts';
import { effectLabel, EffectsEditor } from './EffectsEditor';
import { pickVariant } from './journey';

const fieldLabel = (path: string) => {
  const [head, i] = path.split('.');
  if (head === 'then' && i !== undefined) return `Effect ${Number(i) + 1}`;
  return ({ name: 'Name', when: 'When', then: 'Then', description: 'Description' } as Record<string, string>)[head ?? ''] ?? (path || 'Rule'); // prettier-ignore
};

/** Every question a condition reads, in order of appearance. */
export function condQuestions(c: Cond | undefined, out: string[] = []): string[] {
  if (!c) return out;
  if ('all' in c) c.all.forEach((x) => condQuestions(x, out));
  else if ('any' in c) c.any.forEach((x) => condQuestions(x, out));
  else if ('not' in c) condQuestions(c.not, out);
  else if (c.question && !out.includes(c.question)) out.push(c.question);
  return out;
}

/**
 * The rule editor (task 43): when it fires and what it sets off, tested against a real or made-up
 * assessment before it is saved, with the trace of every condition.
 */
export function RuleEditor({ id }: { id: string }) {
  const d = useItemDraft<RuleDraft>(id, href.rule);
  const questions = useQuestionInfo();

  if (d.item.isPending || !d.draft) return <div className="empty">Loading…</div>;
  if (d.item.isError) return <div className="empty">{d.item.error.message}</div>;

  const r = d.draft;
  const off = !d.owns;
  const issues = ruleIssues(r);
  const set = (patch: Partial<RuleDraft>) => d.setDraft((x) => ({ ...x, ...patch }));

  return (
    <>
      <EditorHead d={d} section="Rules" sectionHref={href.rules()} title={r.name} blocked={issues.length > 0} />
      <div className="builder">
        <div className="stack">
          <section className="card pad">
            <div className="two">
              <div className="field">
                <label htmlFor="re-name">Name</label>
                <input id="re-name" className="input" disabled={off} value={r.name} onChange={(e) => set({ name: e.target.value })} />
              </div>
              <label className="toggle-row mt-auto">
                <input type="checkbox" disabled={off} checked={r.enabled ?? true} onChange={(e) => set({ enabled: e.target.checked })} /> Enabled (a disabled rule is published but never fires)
              </label>
            </div>
            <div className="field">
              <label htmlFor="re-desc">Description</label>
              <textarea id="re-desc" className="input" rows={2} disabled={off} value={r.description ?? ''} onChange={(e) => set({ description: e.target.value })} />
            </div>
          </section>

          <section className="card pad" aria-labelledby="re-when">
            <h2 id="re-when" className="section-title">When</h2>
            <ConditionEditor value={r.when} onChange={(when) => set({ when: when ?? { all: [] } })} questions={questions} mode="rule" disabled={off} emptyLabel="Never (add a condition)" />
          </section>

          <section className="card pad" aria-labelledby="re-then">
            <h2 id="re-then" className="section-title">Then, in this order</h2>
            <EffectsEditor value={r.then} onChange={(then) => set({ then })} disabled={off} min={1} />
          </section>
        </div>

        <aside className="stack">
          <IssueList issues={issues} label={fieldLabel} />
          <TestPanel ruleKey={d.item.data!.key} rule={r} blocked={issues.length > 0} />
        </aside>
      </div>
    </>
  ); // prettier-ignore
}

/** Runs this draft with what-if answers, on an assessment or a made-up Malaysian one. */
function TestPanel({ ruleKey, rule, blocked }: { ruleKey: string; rule: RuleDraft; blocked: boolean }) {
  const { tenant } = useSession();
  const { own } = useStudioAccess();
  const assessments = useAssessments();
  const bodies = useItemBodies('question');
  const run = useDryRun();
  const [target, setTarget] = useState('');
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const keys = condQuestions(rule.when);
  const rows = bodies.data ?? [];
  const defs = Object.fromEntries(keys.map((k) => { const v = pickVariant(rows, k, own); return [k, v ? previewQuestion(v.body as unknown as QuestionDraft) : null]; })); // prettier-ignore

  const go = () =>
    run.mutate({
      ...(target ? { assessment_id: target } : { context: { country: 'MY' } }),
      answers: Object.fromEntries(Object.entries(answers).filter(([k, v]) => keys.includes(k) && v !== undefined).map(([k, v]) => [k, { value: v }])), // prettier-ignore
      rules: { [ruleKey]: rule },
    });
  const result = run.data;

  return (
    <section className="card pad" aria-labelledby="re-test">
      <h2 id="re-test" className="section-title row gap">
        <FlaskConical size={16} /> Test this rule
      </h2>
      <p className="muted small">Uses your unsaved changes. Nothing is stored and nobody is notified.</p>
      <div className="field">
        <label htmlFor="re-target">Test against</label>
        <select id="re-target" className="input" value={target} onChange={(e) => setTarget(e.target.value)}>
          <option value="">A new assessment in Malaysia (only the answers below)</option>
          {tenant?.kind !== 'platform' &&
            (assessments.data ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.customer_name}
              </option>
            ))}
        </select>
      </div>
      {keys.length > 0 && (
        <div className="whatif">
          <div className="eyebrow">{target ? 'Change answers (what if…)' : 'Answers'}</div>
          {keys.map((k) => {
            const q = defs[k];
            return (
              <div key={k} className="whatif-q">
                <div className="small">
                  <span className="mono">{k}</span> {q?.text ?? ''}
                  {answers[k] !== undefined && (
                    <button type="button" className="linklike ml small" onClick={() => setAnswers(({ [k]: _, ...rest }) => rest)}>
                      {target ? 'use the real answer' : 'clear'}
                    </button>
                  )}
                </div>
                {q ? (
                  <AnswerInput id={`wi-${k}`} q={q} value={answers[k]} onChange={(v) => setAnswers({ ...answers, [k]: v })} onPick={(v) => setAnswers({ ...answers, [k]: v })} errors={{}} />
                ) : (
                  <div className="muted small">{bodies.isPending ? 'Loading…' : 'No such question'}</div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <button type="button" className="btn btn-primary mt" onClick={go} disabled={run.isPending || blocked} title={blocked ? 'Fix the problems first' : undefined}>
        {run.isPending ? 'Running…' : 'Run the test'}
      </button>
      {run.isError && <div className="error mt" role="alert">{run.error.message}</div>}
      {result && <Outcome ruleKey={ruleKey} result={result} />}
    </section>
  ); // prettier-ignore
}

function Outcome({ ruleKey, result }: { ruleKey: string; result: DryRunResult }) {
  const mine = result.draft.rules.find((x) => x.rule === ruleKey);
  const before = result.published?.rules.find((x) => x.rule === ruleKey);
  const own = result.issues.filter((i) => i.key === ruleKey);
  if (!mine) return <p className="muted small mt">The rule was not evaluated.</p>;
  return (
    <div className="test-result mt" role="status">
      <div className={`ok-line ${mine.fired ? 'fired' : ''}`}>
        {mine.fired ? <CheckCircle2 size={16} className="ok-t" /> : <MinusCircle size={16} className="muted" />}
        <b>{!mine.enabled ? 'Disabled: not evaluated' : mine.fired ? 'Fires' : 'Does not fire'}</b>
        <span className="muted small">
          as on {result.as_of}
          {before ? ` · published version ${before.fired ? 'fires' : 'does not fire'}` : ' · not published yet'}
        </span>
      </div>
      {mine.trace && (
        <ul className="trace">
          <Trace n={mine.trace} />
        </ul>
      )}
      {mine.effects.length > 0 && (
        <>
          <div className="eyebrow mt">What it sets off</div>
          <ul className="effect-list small">
            {mine.effects.map((e) => (
              <li key={e.id}>{describeEffect(e)}</li>
            ))}
          </ul>
        </>
      )}
      <div className="muted small mt">
        Across all draft rules: {result.diff.started.length} start firing, {result.diff.stopped.length} stop, {result.diff.added.length} effects added, {result.diff.removed.length} removed.
      </div>
      {own.length > 0 && (
        <ul className="problem-list mt">
          {own.map((i, n) => (
            <li key={n}>{i.message}</li>
          ))}
        </ul>
      )}
    </div>
  ); // prettier-ignore
}

function describeEffect(e: FiredEffect) {
  const p = Object.entries(e.params).filter(([, v]) => v !== '' && v !== undefined).map(([k, v]) => `${k} ${typeof v === 'string' ? v : JSON.stringify(v)}`); // prettier-ignore
  return `${effectLabel(e.type)}${p.length ? `: ${p.join(', ')}` : ''}`;
}

const GROUP: Record<string, string> = { all: 'All of', any: 'Any of', not: 'Not' };

function Trace({ n }: { n: TraceNode }) {
  const icon = n.result ? (
    <CheckCircle2 size={14} className="ok-t" />
  ) : (
    <XCircle size={14} className="bad-t" />
  );
  if (n.kind === 'test')
    return (
      <li>
        {icon} <span className="mono small">{n.test}</span>{' '}
        <span className="muted small">— {showActual(n)}</span>
      </li>
    );
  const children = n.kind === 'not' ? [n.child] : n.children;
  return (
    <li>
      {icon} {GROUP[n.kind]}
      <ul>
        {children.map((c, i) => (
          <Trace key={i} n={c} />
        ))}
      </ul>
    </li>
  );
}
